import { sortRoles } from "@/utils/roleUtils";
import type { AnnualEquityValues, Employee, HolidayInclusion, Schedule } from "./types";
import type {
    AnnualConfig,
    EquityResult,
    RoleEquityData,
    RoleGroupColumn,
    SummaryColumn,
    TableCell,
    TableColumn,
    TableData,
    TableRow,
    UserEquityData,
} from "./equityInterfaces";
import type { EquityTargetData } from "./types/needsContrainsRequest";
import type { BusinessAccountExtendedWgItemData, RoleConstraints } from "./types/needsContrains";

const interpolateColorWithAlpha = (
    color1: number[],
    color2: number[],
    factor: number
): string => {
    const result = [...color1];
    for (let i = 0; i < 3; i++) {
        const val1 = result[i] ?? 0;
        const val2 = color2[i] ?? 0;
        result[i] = Math.round(val1 + factor * (val2 - val1));
    }
    return `rgba(${result[0] ?? 0}, ${result[1] ?? 0}, ${result[2] ?? 0}, ${factor})`;
};

const toDateKey = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getDaysFromTimeSlots = (slots: any[] | undefined): string[] => {
    if (!slots) {
        return [];
    }

    const days: string[] = [];

    for (const slot of slots) {
        if (typeof slot === 'string') {
            days.push(slot);
        } else if (slot && (slot.start || slot.end)) {
            const startStr = slot.start || slot.end;
            const endStr = slot.end || slot.start;
            const start = new Date(startStr);
            const end = new Date(endStr);

            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                continue;
            }

            const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            const slotDays = Math.round((endDay.getTime() - startDay.getTime()) / (24 * 60 * 60 * 1000)) + 1;

            for (let i = 0; i < slotDays; i++) {
                days.push(toDateKey(new Date(startDay.getTime() + i * 24 * 60 * 60 * 1000)));
            }
        }
    }
    return days;
};

const isVisibleUser = (visibleUserIds: Set<string> | undefined, userId: string): boolean =>
    !visibleUserIds || visibleUserIds.has(userId);

const sumOf = (values: number[]): number => values.reduce((a, b) => a + b, 0);

const totalWithAverage = (total: number, count: number): string =>
    total + " / " + (count > 0 ? total / count : 0).toFixed(2);

export const generatedScheduleResultToEquity = (schedule: Schedule): EquityResult => {
    const params = new URLSearchParams(globalThis.location.search);
    const startTime = new Date(Number.parseFloat(params.get("fromEpoch") || "0")).setHours(0, 0, 0, 0);
    const endTime = new Date(Number.parseFloat(params.get("toEpoch") || "0")).setHours(0, 0, 0, 0);

    const periodDays = new Set<string>();
    const totalDays = Math.round((endTime - startTime) / (24 * 60 * 60 * 1000)) + 1;

    for (let i = 0; i < totalDays; i++) {
        const day = new Date(startTime + i * 24 * 60 * 60 * 1000);
        periodDays.add(toDateKey(day));
    }

    const blockedDaysMap: Record<string, Set<string>> = {};

    const mapOfUsersEquityData: Record<string, UserEquityData> = {};
    const mapOfRolesEquityData: Record<string, RoleEquityData> = {};
    let totalWeekEndShiftCount = 0;
    let maxUserWeekEndShiftCount = 0;
    let minUserWeekEndShiftCount = 0;
    let totalWeekEndShiftUserCount = 0;
    let totalUsersCount = 0;
    let totalShiftsCount = 0;
    let maxUserFridayShiftCount = 0;
    let minUserFridayShiftCount = 0;
    let totalFridaysUserCount = 0;
    let totalFridays = 0;
    let minUserTotalCount = Number.MAX_SAFE_INTEGER;
    let maxUserTotalCount = 0;
    let totalHolidayShiftCount = 0;
    let totalHolidayShiftUserCount = 0;
    let maxUserHolidayShiftCount = 0;
    let minUserHolidayShiftCount = Number.MAX_SAFE_INTEGER;


    if (schedule) {
        if (schedule.employees) {
            schedule.employees.forEach((item) => {
                const userId = item.userId;
                const roles = item.roles;

                const userEquityData = mapOfUsersEquityData[userId] || (mapOfUsersEquityData[userId] = {
                    rolesMap: {},
                    weekEndShiftCount: 0,
                    fridayShiftCount: 0,
                    holidayShiftCount: 0,
                    totalCount: 0,
                    availableDays: 0,
                    name: item.name || item.userId,
                });

                if (!blockedDaysMap[userId]) {
                    const blockedDays = new Set<string>();
                    blockedDaysMap[userId] = blockedDays;
                    getDaysFromTimeSlots(item.unavailableTimeSlots as any).forEach(d => blockedDays.add(d));
                    getDaysFromTimeSlots(item.undesiredTimeSlots as any).forEach(d => blockedDays.add(d));
                }

                if (roles) {
                    roles.forEach((userRole) => {
                        const roleId = userRole.id;

                        if (!userEquityData.rolesMap[roleId]) {
                            userEquityData.rolesMap[roleId] = {
                                count: 0,
                                declared: true,
                            };
                        } else {
                            userEquityData.rolesMap[roleId].declared = true;
                        }

                        const roleEquityData = mapOfRolesEquityData[roleId] || (mapOfRolesEquityData[roleId] = {
                            usersMap: {},
                            usersCount: 0,
                            minUserCount: 0,
                            maxUserCount: 0,
                            name: userRole.name || userRole.id,
                            totalCount: 0,
                        });

                        if (!roleEquityData.usersMap[userId]) {
                            roleEquityData.usersMap[userId] = {
                                count: 0,
                            };

                            roleEquityData.usersCount++;
                        }
                    });
                }

                totalUsersCount++;
            });
        }

        if (schedule.shifts) {
            schedule.shifts.forEach((item) => {
                let shiftStart = parseFloat(item.startEpoch)
                if (shiftStart < startTime)
                    return
                if (item.employee) {
                    //if ((item.weekendShift || item.holiday)  && item.afterStart) {
                    const userId = item.employee.userId;
                    const userEquityData = mapOfUsersEquityData[userId] || (mapOfUsersEquityData[userId] = {
                        rolesMap: {},
                        weekEndShiftCount: 0,
                        fridayShiftCount: 0,
                        totalCount: 0,
                        availableDays: 0,
                        name: item.employee.name || item.employee.userId,
                    });
                    const userRolesEquityData = userEquityData.rolesMap[item.roleId] || (userEquityData.rolesMap[item.roleId] = {
                        count: 0,
                    });

                    if ((item.weekendShift) && item.afterStart) {
                        if (!userEquityData.weekEndShiftCount) {
                            totalWeekEndShiftUserCount++;
                        }
                        userEquityData.weekEndShiftCount++;
                        totalWeekEndShiftCount++;
                    }
                    if (item.holiday && !item.weekend && item.afterStart) {
                        if (!userEquityData.holidayShiftCount) {
                            totalHolidayShiftUserCount++;
                        }

                        userEquityData.holidayShiftCount++;
                        totalHolidayShiftCount++;
                    }

                    if (item.outOfWg)
                        return;

                    const roleEquityData = mapOfRolesEquityData[item.roleId] || (mapOfRolesEquityData[item.roleId] = {
                        usersMap: {},
                        totalCount: 0,
                        usersCount: 0,
                        minUserCount: 0,
                        maxUserCount: 0,
                        name: item.roleName || item.roleId,
                    });

                    let roleUsersEquityData = roleEquityData.usersMap[userId];

                    if (!roleUsersEquityData) {
                        roleUsersEquityData = roleEquityData.usersMap[userId] = {
                            count: 0,
                        };
                        roleEquityData.usersCount++;
                    }

                    userRolesEquityData.count++;
                    userEquityData.totalCount++;
                    roleEquityData.totalCount++;
                    roleUsersEquityData.count++;

                    if ((new Date(item.start)).getDay() == 5) {
                        if (!userEquityData.fridayShiftCount) {
                            totalFridaysUserCount++;
                        }
                        userEquityData.fridayShiftCount++;
                        totalFridays++;
                    }

                    totalShiftsCount++;
                }
            });
        }

        if (schedule.shifts) {
            schedule.shifts.forEach((item) => {

                if (item.employee && item.shiftId && !item.outOfWg) {
                    const shiftStart = parseFloat(item.startEpoch);

                    if (shiftStart >= startTime) {
                        const blocked = blockedDaysMap[item.employee.userId];

                        if (blocked) {
                            blocked.add(toDateKey(new Date(shiftStart)));
                        }
                    }
                }
            });
        }
    }

    // Calculate available days per user
    Object.entries(mapOfUsersEquityData).forEach(([userId, userData]) => {
        const blocked = blockedDaysMap[userId] || new Set();
        let blockedInPeriod = 0;

        for (const day of blocked) {
            if (periodDays.has(day)) {
                blockedInPeriod++;
            }
        }

        userData.availableDays = periodDays.size - blockedInPeriod;
    });

    Object.entries(mapOfUsersEquityData).forEach(([, equityItem]) => {
        if (equityItem.holidayShiftCount < minUserHolidayShiftCount) {
            minUserHolidayShiftCount = equityItem.holidayShiftCount;
        }

        if (equityItem.holidayShiftCount > maxUserHolidayShiftCount) {
            maxUserHolidayShiftCount = equityItem.holidayShiftCount;
        }
        if (equityItem.weekEndShiftCount < minUserWeekEndShiftCount) {
            minUserWeekEndShiftCount = equityItem.weekEndShiftCount;
        }
        if (equityItem.weekEndShiftCount > maxUserWeekEndShiftCount) {
            maxUserWeekEndShiftCount = equityItem.weekEndShiftCount;
        }

        if (equityItem.fridayShiftCount < minUserFridayShiftCount) {
            minUserFridayShiftCount = equityItem.fridayShiftCount;
        }
        if (equityItem.fridayShiftCount > maxUserFridayShiftCount) {
            maxUserFridayShiftCount = equityItem.fridayShiftCount;
        }

        if (equityItem.totalCount < minUserTotalCount) {
            minUserTotalCount = equityItem.totalCount;
        }
        if (equityItem.totalCount > maxUserTotalCount) {
            maxUserTotalCount = equityItem.totalCount;
        }

        Object.entries(equityItem.rolesMap).forEach(([roleId, roleInfo]) => {
            const roleEquity = mapOfRolesEquityData[roleId];
            if (roleEquity) {
                if (roleInfo.count < roleEquity.minUserCount) {
                    roleEquity.minUserCount = roleInfo.count;
                }
                if (roleInfo.count > roleEquity.maxUserCount) {
                    roleEquity.maxUserCount = roleInfo.count;
                }
            }
        });
    });

    Object.entries(mapOfRolesEquityData).forEach(([, roleEquity]) => {
        roleEquity.minMaxUserRange = roleEquity.maxUserCount - roleEquity.minUserCount;
    });

    if (minUserHolidayShiftCount === Number.MAX_SAFE_INTEGER) {
        minUserHolidayShiftCount = 0;
    }

    return {
        mapOfUsersEquityData,
        mapOfRolesEquityData,
        totalDays,
        totalWeekEndShiftCount,
        totalWeekEndShiftUserCount,
        totalUsersCount,
        minUserWeekEndShiftCount,
        maxUserWeekEndShiftCount,
        totalHolidayShiftCount,
        totalHolidayShiftUserCount,
        totalFridaysUserCount,
        totalFridays,
        minUserFridayShiftCount,
        maxUserFridayShiftCount,
        minUserHolidayShiftCount,
        maxUserHolidayShiftCount,
        totalShiftsCount,
        minUserTotalCount: minUserTotalCount === Number.MAX_SAFE_INTEGER ? 0 : minUserTotalCount,
        maxUserTotalCount,
    };
};

export const equityToTableData = (
    equityResult: EquityResult,
    roleOrders?: Map<string, number>,
    targets?: EquityTargetData,
    visibleUserIds?: Set<string>
): TableData => {
    const totalUsersCount = equityResult.totalUsersCount;
    const mapOfUsersEquityData = equityResult.mapOfUsersEquityData;
    const mapOfRolesEquityData = equityResult.mapOfRolesEquityData;

    const columns: TableColumn[] = [{ type: "name", label: "Nom/Rôle" }];
    columns.push({ type: "availableDays", label: "Jours dispos" });
    columns.push({ type: "totalShifts", label: "Quarts total" });
    columns.push({ type: "weekenedShifts", label: "Quart de FDS" });
    columns.push({ type: "holidayShifts", label: "Quart Férié" });
    const roleColumns: TableColumn[] = [];
    const rows: TableRow[] = [];

    const sortedRoles = sortRoles(Object.entries(mapOfRolesEquityData), roleOrders || new Map(), (r) => r[0], (r) => r[1].name);

    sortedRoles.forEach(([roleId, equityItem]) => {
        const col = { id: roleId, label: equityItem.name, type: "role" };
        columns.push(col);
        roleColumns.push(col);
    });

    const totalShiftAverage = totalUsersCount > 0 ? equityResult.totalShiftsCount / totalUsersCount : 0;

    const weekendShiftAvr = totalUsersCount > 0 ? equityResult.totalWeekEndShiftCount / totalUsersCount : 0;
    const weekendShiftMinMaxRange = equityResult.maxUserWeekEndShiftCount - equityResult.minUserWeekEndShiftCount;

    const holidayShiftAvr = totalUsersCount > 0
        ? equityResult.totalHolidayShiftCount / totalUsersCount
        : 0;

    const holidayShiftMinMaxRange =
        equityResult.maxUserHolidayShiftCount -
        equityResult.minUserHolidayShiftCount;

    const totalShiftMinMaxRange = equityResult.maxUserTotalCount - equityResult.minUserTotalCount;

    const createCellValueWithColor = (value: number, avr: number, range: number, userId?: string, columnId?: string): TableCell => {
        const deviation = value - avr;
        const absDeviation = Math.abs(deviation);
        const bgColor = interpolateColorWithAlpha(
            [0, 255, 0],
            [255, 120, 0],
            range > 0 ? absDeviation / range : 0
        );
        const target = userId && columnId && targets ? targets[userId]?.[columnId] : undefined;
        return {
            value: value + "",
            bgColor: bgColor,
            target,
            userId,
            columnId,
        };
    };

    Object.entries(mapOfUsersEquityData).forEach(([userId, equityItem]) => {
        const rowItem: TableRow = { columns: [] };

        rowItem.columns.push({ value: equityItem.name, userId });
        rowItem.columns.push({ value: equityItem.availableDays + "" });
        rowItem.columns.push(createCellValueWithColor(equityItem.totalCount, totalShiftAverage, totalShiftMinMaxRange, userId, "totalShifts"));
        rowItem.columns.push(createCellValueWithColor(equityItem.weekEndShiftCount, weekendShiftAvr, weekendShiftMinMaxRange, userId, "weekendShifts"));
        rowItem.columns.push(
            createCellValueWithColor(
                equityItem.holidayShiftCount,
                holidayShiftAvr,
                holidayShiftMinMaxRange,
                userId,
                "holidayShifts"
            )
        );

        roleColumns.forEach((col) => {
            const roleEquity = mapOfRolesEquityData[col.id!];
            const userRoleEquityItem = equityItem.rolesMap[col.id!];
            const roleAvr = roleEquity && roleEquity.usersCount ? roleEquity.totalCount / roleEquity.usersCount : 0;

            if (userRoleEquityItem && (userRoleEquityItem.declared || userRoleEquityItem.count > 0)) {
                const cellValue = createCellValueWithColor(
                    userRoleEquityItem.count,
                    roleAvr,
                    roleEquity && roleEquity.minMaxUserRange !== undefined ? roleEquity.minMaxUserRange : roleAvr,
                    userId,
                    col.id
                );
                cellValue.eligible = !!userRoleEquityItem.declared;
                rowItem.columns.push(cellValue);
            } else {
                rowItem.columns.push({ value: "-" });
            }
        });

        rows.push(rowItem);
    });

    rows.sort((a, b) => {
        const lastNameA = (a.columns[0]?.value ?? "").split(" ")[1] ?? "";
        const lastNameB = (b.columns[0]?.value ?? "").split(" ")[1] ?? "";
        return lastNameA.localeCompare(lastNameB, undefined, { sensitivity: "base" });
    });
    const visibleUsers = Object.entries(mapOfUsersEquityData)
        .filter(([userId]) => isVisibleUser(visibleUserIds, userId));
    const visibleUsersCount = visibleUserIds ? visibleUsers.length : totalUsersCount;

    const totalRow: TableRow = { columns: [{ value: "Total/Moyenne" }] };
    totalRow.columns.push({ value: "" });
    // Unfiltered totals come from equityResult: some users carry a NaN counter that a per-user sum would drop.
    const totalOf = (globalTotal: number, pick: (equityItem: UserEquityData) => number): number =>
        visibleUserIds ? sumOf(visibleUsers.map(([, equityItem]) => pick(equityItem) || 0)) : globalTotal;
    totalRow.columns.push({
        value: totalWithAverage(totalOf(equityResult.totalShiftsCount, (item) => item.totalCount), visibleUsersCount)
    });
    // Colonnes temporairement masquées
    totalRow.columns.push({
        value: totalWithAverage(totalOf(equityResult.totalWeekEndShiftCount, (item) => item.weekEndShiftCount), visibleUsersCount)
    });
    totalRow.columns.push({
        value: totalWithAverage(totalOf(equityResult.totalHolidayShiftCount, (item) => item.holidayShiftCount), visibleUsersCount)
    });
    rows.push(totalRow);
    sortedRoles.forEach(([, roleEquity]) => {
        const counts = visibleUsers
            .filter(([userId]) => roleEquity.usersMap[userId])
            .map(([userId]) => roleEquity.usersMap[userId]!.count);
        totalRow.columns.push({ value: totalWithAverage(sumOf(counts), counts.length) });
    });

    // Colonnes temporairement masquées
    //totalRow.columns.push({ value: equityResult.totalWeekEndShiftCount + "" + " / " + weekendShiftAvr.toFixed(2) });

    return {
        columns: columns,
        rows: rows,
    };
};

const annualScore = (values: AnnualEquityValues | undefined, dayIds: string[]): number => {
    if (!values) {
        return 0;
    }
    if (!values.daysOfWeek) {
        return values.score ?? 0;
    }
    return sumOf(dayIds.map((dayId) => values.daysOfWeek?.[dayId === "0" ? "7" : dayId]?.score ?? 0));
};

// Shift counts since January 1st come precomputed in the payload, one entry per role.
const annualShiftCount = (
    employee: Employee | undefined,
    roleId: string,
    holiday: HolidayInclusion,
    dayIds: string[]
): number => {
    const stats = employee?.statsByRoleId?.[roleId]?.startOfYearAnnualEquity;
    return (holiday === "Seulement" ? 0 : annualScore(stats?.normal, dayIds))
        + (holiday === "Exclure" ? 0 : annualScore(stats?.holiday, dayIds));
};

const matchHoliday = (rule: HolidayInclusion, isHoliday: boolean): boolean => {
    if (rule === "Exclure") {
        return !isHoliday;
    }
    if (rule === "Seulement") {
        return isHoliday;
    }
    return true;
};

const countPeriodShiftsByColumn = (
    schedule: Schedule,
    columns: SummaryColumn[]
): Record<string, Record<string, number>> => {
    const params = new URLSearchParams(globalThis.location.search);
    const startTime = new Date(Number.parseFloat(params.get("fromEpoch") || "0")).setHours(0, 0, 0, 0);
    const holidaySet = new Set(schedule?.holidays ?? []);

    const counts: Record<string, Record<string, number>> = {};
    schedule?.shifts?.forEach((shift) => {
        if (Number.parseFloat(shift.startEpoch) < startTime) {
            return;
        }
        if (shift.outOfWg) {
            return;
        }
        if (!shift.employee) {
            return;
        }

        const userId = shift.employee.userId;
        const dayId = String(new Date(shift.start).getDay());
        const isHoliday = holidaySet.has(toDateKey(new Date(shift.start)));

        columns.forEach((col) => {
            const roleNeeds = col.mapOfNeedSettings?.[shift.roleId];
            if (!roleNeeds?.mapOfDayOptions?.[dayId]?.apply) {
                return;
            }
            if (!matchHoliday(col.holiday, isHoliday)) {
                return;
            }
            (counts[userId] ||= {})[col.id] = (counts[userId]?.[col.id] || 0) + 1;
        });
    });

    return counts;
};

const ALL_DAY_IDS = ["0", "1", "2", "3", "4", "5", "6"];

export const withDefaultAnnualColumns = (
    columns: SummaryColumn[],
    seededRoleIds: string[],
    equityResult: EquityResult,
    roleOrders?: Map<string, number>
): AnnualConfig | null => {
    const seeded = new Set(seededRoleIds);
    const existingIds = new Set(columns.map((col) => col.id));
    const missingRoles = sortRoles(
        Object.entries(equityResult.mapOfRolesEquityData),
        roleOrders || new Map(),
        (r) => r[0],
        (r) => r[1].name
    ).filter(([roleId]) => !seeded.has(roleId));

    if (!missingRoles.length) {
        return null;
    }

    const defaults: SummaryColumn[] = missingRoles
        .filter(([roleId]) => !existingIds.has(`role_${roleId}`))
        .map(([roleId, roleEquity]) => ({
            id: `role_${roleId}`,
            label: roleEquity.name,
            order: 0,
            holiday: "Inclure",
            mapOfNeedSettings: {
                [roleId]: {
                    apply: true,
                    mapOfDayOptions: Object.fromEntries(ALL_DAY_IDS.map((dayId) => [dayId, { apply: true }])),
                },
            },
        }));
    const sorted = [...columns].sort((a, b) => a.order - b.order);
    // The very first defaults go in front of the columns already saved; roles created later go at the end.
    const ordered = seeded.size === 0 ? [...defaults, ...sorted] : [...sorted, ...defaults];

    return {
        columns: ordered.map((col, order) => ({ ...col, order })),
        seededRoleIds: [...seededRoleIds, ...missingRoles.map(([roleId]) => roleId)],
    };
};

export const annualToTableData = (
    schedule: Schedule,
    equityResult: EquityResult,
    customColumns: SummaryColumn[],
    visibleUserIds?: Set<string>
): TableData => {
    const employeesByUserId = new Map<string, Employee>(
        (schedule?.employees ?? []).map((employee) => [employee.userId, employee])
    );

    const columns: TableColumn[] = [{ type: "name", label: "Nom/Rôle" }];

    const sortedCustomColumns = [...customColumns].sort((a, b) => a.order - b.order);
    sortedCustomColumns.forEach((col) => {
        columns.push({ id: col.id, label: col.label || "Sans nom", type: "annualCustom" });
    });
    const dataColumnsCount = columns.length - 1;
    columns.push({ type: "annualAdd", label: "" });

    const customPeriodCounts = countPeriodShiftsByColumn(schedule, sortedCustomColumns);
    const customRoleDays = new Map(sortedCustomColumns.map((col) => [
        col.id,
        Object.entries(col.mapOfNeedSettings || {})
            .map(([roleId, roleDays]) => ({
                roleId,
                dayIds: Object.entries(roleDays?.mapOfDayOptions || {})
                    .filter(([, day]) => day.apply)
                    .map(([dayId]) => dayId),
            }))
            .filter(({ dayIds }) => dayIds.length > 0),
    ]));

    const countsByRoleId = new Map<string, number[]>();

    const rows: TableRow[] = Object.entries(equityResult.mapOfUsersEquityData).map(([userId, equityItem]) => {
        const employee = employeesByUserId.get(userId);
        const row: TableRow = { columns: [{ value: equityItem.name, userId }] };

        sortedCustomColumns.forEach((col) => {
            const roleDays = customRoleDays.get(col.id) ?? [];
            const declared = roleDays.some(({ roleId }) => equityItem.rolesMap[roleId]?.declared);
            const count = sumOf(roleDays.map(({ roleId, dayIds }) => annualShiftCount(employee, roleId, col.holiday, dayIds)));
            const periodCount = customPeriodCounts[userId]?.[col.id] ?? 0;

            if (!declared && count === 0 && periodCount === 0) {
                row.columns.push({ value: "-" });
                return;
            }

            const counts = countsByRoleId.get(col.id) ?? [];
            counts.push(count);
            countsByRoleId.set(col.id, counts);

            row.columns.push({
                value: periodCount > 0 ? count + periodCount + " / " + count : count + "",
                annualCount: count,
                userId,
                columnId: col.id,
            });
        });

        row.columns.push({ value: "" });

        return row;
    });

    rows.sort((a, b) => {
        const lastNameA = (a.columns[0]?.value ?? "").split(" ")[1] ?? "";
        const lastNameB = (b.columns[0]?.value ?? "").split(" ")[1] ?? "";
        return lastNameA.localeCompare(lastNameB, undefined, { sensitivity: "base" });
    });

    const statsByRoleId = new Map(
        [...countsByRoleId].map(([roleId, counts]) => {
            const total = counts.reduce((sum, count) => sum + count, 0);
            return [roleId, {
                total,
                average: counts.length > 0 ? total / counts.length : 0,
                range: Math.max(...counts) - Math.min(...counts),
            }];
        })
    );

    // Colour each count against its role average, like the other equity tabs do.
    rows.forEach((row) => {
        row.columns.forEach((cell) => {
            const stats = cell.columnId ? statsByRoleId.get(cell.columnId) : undefined;
            if (!stats || cell.annualCount === undefined) {
                return;
            }
            cell.bgColor = interpolateColorWithAlpha(
                [0, 255, 0],
                [255, 120, 0],
                stats.range > 0 ? Math.abs(cell.annualCount - stats.average) / stats.range : 0
            );
        });
    });

    const visibleRows = rows.filter((row) => isVisibleUser(visibleUserIds, row.columns[0]!.userId!));
    const totalRow: TableRow = { columns: [{ value: "Total/Moyenne" }] };
    for (let columnIndex = 1; columnIndex <= dataColumnsCount; columnIndex++) {
        const counts = visibleRows
            .map((row) => row.columns[columnIndex]?.annualCount)
            .filter((count): count is number => count !== undefined);
        totalRow.columns.push({ value: totalWithAverage(sumOf(counts), counts.length) });
    }
    totalRow.columns.push({ value: "" });
    rows.push(totalRow);

    return { columns, rows };
};

export const summaryToTableData = (
    schedule: Schedule,
    columns: SummaryColumn[],
    equityResult: EquityResult,
    visibleUserIds?: Set<string>
): TableData => {
    const sortedColumns = [...columns].sort((a, b) => a.order - b.order);
    const counts = countPeriodShiftsByColumn(schedule, sortedColumns);

    const userEntries = Object.entries(equityResult.mapOfUsersEquityData);

    const colStats: Record<string, { avg: number; range: number }> = {};
    sortedColumns.forEach((col) => {
        const values = userEntries.map(([userId]) => counts[userId]?.[col.id] || 0);
        const sum = values.reduce((a, b) => a + b, 0);
        const avg = values.length ? sum / values.length : 0;
        const range = values.length ? Math.max(...values) - Math.min(...values) : 0;
        colStats[col.id] = { avg, range };
    });

    const tableColumns: TableColumn[] = [
        { type: "name", label: "Nom/Rôle" },
        { type: "availableDays", label: "Jours dispos" },
    ];
    sortedColumns.forEach((col) => {
        tableColumns.push({ id: col.id, label: col.label || "Sans nom", type: "summary" });
    });
    tableColumns.push({ type: "summaryAdd", label: "" });

    const rows: TableRow[] = [];
    userEntries.forEach(([userId, equityItem]) => {
        const rowItem: TableRow = { columns: [] };
        rowItem.columns.push({ value: equityItem.name, userId });
        rowItem.columns.push({ value: equityItem.availableDays + "" });

        sortedColumns.forEach((col) => {
            const value = counts[userId]?.[col.id] || 0;
            const stats = colStats[col.id] ?? { avg: 0, range: 0 };
            const bgColor = interpolateColorWithAlpha(
                [0, 255, 0],
                [255, 120, 0],
                stats.range > 0 ? Math.abs(value - stats.avg) / stats.range : 0
            );
            rowItem.columns.push({ value: value + "", bgColor, columnId: col.id });
        });

        rowItem.columns.push({ value: "" }); // colonne "+"
        rows.push(rowItem);
    });

    rows.sort((a, b) =>
        (a.columns[0]?.value ?? "").localeCompare(b.columns[0]?.value ?? "", undefined, { sensitivity: "base" })
    );
    rows.sort((a, b) => {
        const lastNameA = (a.columns[0]?.value ?? "").split(" ")[1] ?? "";
        const lastNameB = (b.columns[0]?.value ?? "").split(" ")[1] ?? "";
        return lastNameA.localeCompare(lastNameB, undefined, { sensitivity: "base" });
    });

    const visibleEntries = userEntries.filter(([userId]) => isVisibleUser(visibleUserIds, userId));

    const totalRow: TableRow = { columns: [{ value: "Total", isTotal: true }] };
    totalRow.columns.push({
        value: sumOf(visibleEntries.map(([, equityItem]) => equityItem.availableDays)) + "",
        isTotal: true,
    });
    sortedColumns.forEach((col) => {
        totalRow.columns.push({
            value: sumOf(visibleEntries.map(([userId]) => counts[userId]?.[col.id] || 0)) + "",
            isTotal: true,
        });
    });
    totalRow.columns.push({ value: "", isTotal: true });
    rows.push(totalRow);

    return { columns: tableColumns, rows };
};

const resolveDayNeed = (
    roleConstraints: RoleConstraints | undefined,
    dateKey: string,
    dayId: string,
    isHoliday: boolean
): number => {
    const group = roleConstraints?.dicOfNeeds?.["default"];

    if (!group || group.active === "false" || group.apply === "false") {
        return 0;
    }

    const override = group.dicOfSpecificDays?.[dateKey];
    const config = override && override.active !== "false"
        ? override
        : group.dicOfDaysOfWeek?.[dayId];

    if (!config || config.active === "false" || config.apply === "false") {
        return 0;
    }

    const holidayException = config.exceptions?.holiday;

    if (isHoliday && holidayException && holidayException.active !== "false" && holidayException.actionIfMatch === "skip") {
        return 0;
    }

    return Math.max(Number(config.min) || 0, Number(config.max) || 0);
};

export const computeRoleGroupPrefill = (
    schedule: Schedule | null,
    column: SummaryColumn,
    equityResult: EquityResult,
    refDays: number,
    desiredTargets: Record<string, number>,
    needsData: BusinessAccountExtendedWgItemData | null
): Record<string, number> | null => {
    const params = new URLSearchParams(globalThis.location.search);
    const startTime = new Date(Number.parseFloat(params.get("fromEpoch") || "0")).setHours(0, 0, 0, 0);

    const holidaySet = new Set(schedule?.holidays ?? []);
    const totalDays = equityResult.totalDays > 0 ? equityResult.totalDays : 1;
    const roleIds = Object.keys(column.mapOfNeedSettings || {});

    const declaredRoles: Record<string, Set<string>> = {};

    (schedule?.employees ?? []).forEach((emp) => {
        declaredRoles[emp.userId] = new Set((emp.roles ?? []).map((r) => r.id));
    });

    const rolesTemplate = needsData?.dicOfNeedsConstraintsTemplates?.["default"]?.dicOfRoles ?? {};

    let periodNeed = 0;

    for (let i = 0; i < totalDays; i++) {
        const date = new Date(startTime);
        date.setDate(date.getDate() + i);

        const dayId = String(date.getDay());
        const dateKey = toDateKey(date);
        const isHoliday = holidaySet.has(dateKey);

        if (!matchHoliday(column.holiday, isHoliday)) {
            continue;
        }

        roleIds.forEach((roleId) => {
            if (!column.mapOfNeedSettings?.[roleId]?.mapOfDayOptions?.[dayId]?.apply) {
                return;
            }

            periodNeed += resolveDayNeed(rolesTemplate[roleId], dateKey, dayId, isHoliday);
        });
    }

    const columnNeed = Math.round((periodNeed * refDays) / totalDays);

    const eligibleUserIds = Object.keys(equityResult.mapOfUsersEquityData)
        .filter((userId) => roleIds.some((roleId) => declaredRoles[userId]?.has(roleId)));

    if (!eligibleUserIds.length || columnNeed <= 0) {
        return null;
    }

    const positiveDesired = eligibleUserIds
        .map((userId) => desiredTargets[userId])
        .filter((value): value is number => value !== undefined && value > 0);

    const averageDesired = positiveDesired.length
        ? positiveDesired.reduce((sum, value) => sum + value, 0) / positiveDesired.length
        : 1;

    const weights: Record<string, number> = {};

    eligibleUserIds.forEach((userId) => {
        weights[userId] = desiredTargets[userId] ?? averageDesired;
    });

    const totalWeight = eligibleUserIds.reduce((sum, userId) => sum + (weights[userId] ?? 0), 0);

    const shares = eligibleUserIds.map((userId) => ({
        userId,
        share: totalWeight > 0
            ? (columnNeed * (weights[userId] ?? 0)) / totalWeight
            : 0,
    }));

    const targets: Record<string, number> = {};

    shares.forEach(({ userId, share }) => {
        targets[userId] = Math.min(999, Math.max(0, Math.floor(share)));
    });

    let remainder = columnNeed - Object.values(targets).reduce((sum, value) => sum + value, 0);

    [...shares]
        .sort((a, b) => (b.share % 1) - (a.share % 1))
        .forEach(({ userId }) => {
            if (remainder > 0 && (targets[userId] ?? 0) < 999) {
                targets[userId] = (targets[userId] ?? 0) + 1;
                remainder--;
            }
        });

    return targets;
};


export function weeklyToTableData(
    schedule: Schedule,
    visibleUserIds?: Set<string>,
    visibleRoleIds?: Set<string>
): TableData {
    const employees = schedule.employees ?? [];
    const shifts = schedule.shifts ?? [];

    // weekKey -> a real date string we can sort/format by (Monday of that week)
    const weekKeyToStartDate = new Map<string, string>();
    // userId -> weekKey -> summed hours
    const hoursByUserAndWeek = new Map<string, Map<string, number>>();

    for (const shift of shifts) {
        if (!shift.afterStart) continue;
        if (shift.roleName.includes("Externe")) continue;
        //if (shift.roleName.includes("LIB")) continue;
        if (shift.roleName.includes("*") && !shift.roleName.includes("FDS")) continue;

        const userId = shift.employee?.userId;
        if (!userId) continue;

        // shift.startOfWeek is the Sunday before the week's Monday, so add 1 day.
        const [y, m, d] = shift.startOfWeek.split("-").map(Number);
        const mondayDate = new Date(y, m - 1, d + 1);
        const weekKey = `${mondayDate.getFullYear()}-${String(mondayDate.getMonth() + 1).padStart(2, "0")}-${String(mondayDate.getDate()).padStart(2, "0")}`;

        if (!weekKeyToStartDate.has(weekKey)) {
            weekKeyToStartDate.set(weekKey, weekKey);
        }

        if (visibleRoleIds && !visibleRoleIds.has(shift.roleId)) continue;

        const hours = shift.length ?? 0;
        if (!hoursByUserAndWeek.has(userId)) {
            hoursByUserAndWeek.set(userId, new Map());
        }
        const userWeeks = hoursByUserAndWeek.get(userId)!;
        userWeeks.set(weekKey, (userWeeks.get(weekKey) ?? 0) + hours);
    }

    const sortedWeekKeys = Array.from(weekKeyToStartDate.keys()).sort(
        (a, b) => a.localeCompare(b) // "YYYY-MM-DD" strings sort chronologically as plain strings
    );

    const formatWeekLabel = (startDateStr: string) => {
        const [y, m, d] = startDateStr.split("-").map(Number);
        const start = new Date(y, m - 1, d);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        const fmt = (d: Date) => `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
        return `${fmt(start)} - ${fmt(end)}`;
    };

    const columns: TableColumn[] = [
        { label: "Employé", type: "name" },
        ...sortedWeekKeys.map((key) => ({
            id: key,
            label: formatWeekLabel(weekKeyToStartDate.get(key)!),
            type: "week",
        })),
        { label: "Total", type: "weekTotal" },
    ];

    const rows: TableRow[] = employees.map((employee) => {
        const userWeeks = hoursByUserAndWeek.get(employee.userId) ?? new Map<string, number>();
        let total = 0;

        const weekCells: TableCell[] = sortedWeekKeys.map((key) => {
            const hours = userWeeks.get(key) ?? 0;
            total += hours;
            return { value: hours ? `${hours}h` : "" };
        });

        return {
            columns: [
                {
                    value: employee.name ?? `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim(),
                    userId: employee.userId,
                },
                ...weekCells,
                { value: `${total}h`, isTotal: true },
            ],
        };
    });

    // "Total/Moyenne" row: sum of the visible people, per week + grand total
    let grandTotal = 0;
    const totalRowCells: TableCell[] = sortedWeekKeys.map((key) => {
        let sum = 0;
        hoursByUserAndWeek.forEach((userWeeks, userId) => {
            if (isVisibleUser(visibleUserIds, userId)) {
                sum += userWeeks.get(key) ?? 0;
            }
        });
        grandTotal += sum;
        return { value: `${sum}h`, isTotal: true };
    });

    rows.push({
        columns: [
            { value: "Total/Moyenne", isTotal: true },
            ...totalRowCells,
            { value: `${grandTotal}h`, isTotal: true },
        ],
    });

    return { columns, rows };
}

export const roleGroupToTableData = (
    schedule: Schedule,
    columns: RoleGroupColumn[],
    equityResult: EquityResult,
    refDays: number,
    desiredTargets: Record<string, number>,
    visibleUserIds?: Set<string>
): TableData => {
    const params = new URLSearchParams(globalThis.location.search);
    const startTime = new Date(Number.parseFloat(params.get("fromEpoch") || "0")).setHours(0, 0, 0, 0);
    const holidaySet = new Set(schedule?.holidays ?? []);
    const totalDays = equityResult.totalDays > 0 ? equityResult.totalDays : 1;

    const sortedColumns = [...columns].sort((a, b) => a.order - b.order);

    const colRoleIds: Record<string, string[]> = {};
    sortedColumns.forEach((col) => {
        colRoleIds[col.id] = Object.keys(col.mapOfNeedSettings || {});
    });

    const declaredRoles: Record<string, Set<string>> = {};
    (schedule?.employees ?? []).forEach((emp) => {
        declaredRoles[emp.userId] = new Set((emp.roles ?? []).map((r) => r.id));
    });

    const counts: Record<string, Record<string, number>> = {};
    const columnShiftTotals: Record<string, number> = {}; // NEW: total shifts per column, any/no employee

    schedule?.shifts?.forEach((shift) => {
        if (Number.parseFloat(shift.startEpoch) < startTime) {
            return;
        }
        if (shift.outOfWg) {
            return;
        }

        const dayId = String(new Date(shift.start).getDay());
        const isHoliday = holidaySet.has(toDateKey(new Date(shift.start)));

        sortedColumns.forEach((col) => {
            const roleNeeds = col.mapOfNeedSettings?.[shift.roleId];
            if (!roleNeeds?.mapOfDayOptions?.[dayId]?.apply) {
                return;
            }
            if (!matchHoliday(col.holiday, isHoliday)) {
                return;
            }
            // NEW: count this shift toward the column total regardless of employee
            columnShiftTotals[col.id] = (columnShiftTotals[col.id] || 0) + 1;
        });

        if (!shift.employee) {
            return;
        }

        const userId = shift.employee.userId;

        sortedColumns.forEach((col) => {
            const roleNeeds = col.mapOfNeedSettings?.[shift.roleId];
            if (!roleNeeds?.mapOfDayOptions?.[dayId]?.apply) {
                return;
            }
            if (!matchHoliday(col.holiday, isHoliday)) {
                return;
            }
            (counts[userId] ||= {})[col.id] = (counts[userId]?.[col.id] || 0) + 1;
        });
    });

    const userEntries = Object.entries(equityResult.mapOfUsersEquityData);

    const isEligible = (userId: string, colId: string): boolean =>
        colRoleIds[colId]?.some((roleId) => declaredRoles[userId]?.has(roleId)) ?? false;

    const perRefPeriod = (value: number): number => Math.round((value * refDays) / totalDays);

    const rawCount = (userId: string, colId: string): number => counts[userId]?.[colId] || 0;

    const normAvg = (userId: string, colId: string): number => perRefPeriod(rawCount(userId, colId));

    const withTotal = (total: number, avg: number): string => `${total} / ${avg}`;

    const withTotalPlus = (total: number, avg: number, allTotal : number): string => `${total} / ${avg} / ${allTotal}`;

    const colStats: Record<string, { avg: number; range: number }> = {};
    sortedColumns.forEach((col) => {
        const values = userEntries
            .filter(([userId]) => isEligible(userId, col.id))
            .map(([userId]) => normAvg(userId, col.id));
        const sum = values.reduce((a, b) => a + b, 0);
        const avg = values.length ? sum / values.length : 0;
        const range = values.length ? Math.max(...values) - Math.min(...values) : 0;
        colStats[col.id] = { avg, range };
    });

    const tableColumns: TableColumn[] = [
        { type: "name", label: "Nom/Rôle" },
        { type: "roleGroupDesired", label: `Qté désirée par ${refDays} jours` },
    ];
    sortedColumns.forEach((col) => {
        tableColumns.push({ id: col.id, label: col.label || "Sans nom", type: "roleGroup" });
    });
    tableColumns.push({ type: "roleGroupAdd", label: "" });

    const rows: TableRow[] = [];
    userEntries.forEach(([userId, equityItem]) => {
        const rowItem: TableRow = { columns: [] };
        rowItem.columns.push({ value: equityItem.name, userId });
        rowItem.columns.push({
            value: withTotal(equityItem.totalCount, perRefPeriod(equityItem.totalCount)),
            userId,
            desiredTarget: desiredTargets[userId],
        });

        sortedColumns.forEach((col) => {
            if (!isEligible(userId, col.id)) {
                rowItem.columns.push({ value: "-", eligible: false, columnId: col.id });
                return;
            }
            const value = normAvg(userId, col.id);
            const stats = colStats[col.id] ?? { avg: 0, range: 0 };
            const bgColor = interpolateColorWithAlpha(
                [0, 255, 0],
                [255, 120, 0],
                stats.range > 0 ? Math.abs(value - stats.avg) / stats.range : 0
            );
            rowItem.columns.push({
                value: withTotal(rawCount(userId, col.id), value),
                bgColor,
                userId,
                columnId: col.id,
                eligible: true,
                roleGroupTarget: col.targets?.[userId],
            });
        });

        rowItem.columns.push({ value: "" });
        rows.push(rowItem);
    });

    rows.sort((a, b) =>
        (a.columns[0]?.value ?? "").localeCompare(b.columns[0]?.value ?? "", undefined, { sensitivity: "base" })
    );
    rows.sort((a, b) => {
        const lastNameA = (a.columns[0]?.value ?? "").split(" ")[1] ?? "";
        const lastNameB = (b.columns[0]?.value ?? "").split(" ")[1] ?? "";
        return lastNameA.localeCompare(lastNameB, undefined, { sensitivity: "base" });
    });

    const visibleEntries = userEntries.filter(([userId]) => isVisibleUser(visibleUserIds, userId));

    const totalRow: TableRow = { columns: [{ value: "Total", isTotal: true }] };
    totalRow.columns.push({
        value: withTotal(
            sumOf(visibleEntries.map(([, equityItem]) => equityItem.totalCount)),
            sumOf(visibleEntries.map(([userId]) => desiredTargets[userId] ?? 0))
        ),
        isTotal: true,
    });
    sortedColumns.forEach((col) => {
        // Only eligible users get a count and a manual entry, so only they are summed.
        const eligibleUserIds = visibleEntries
            .map(([userId]) => userId)
            .filter((userId) => isEligible(userId, col.id));
        totalRow.columns.push({
            value: withTotalPlus(
                sumOf(eligibleUserIds.map((userId) => rawCount(userId, col.id))),
                sumOf(eligibleUserIds.map((userId) => col.targets?.[userId] ?? 0)),
                columnShiftTotals[col.id] || 0
            ),
            isTotal: true,
        });
    });
    totalRow.columns.push({ value: "", isTotal: true });
    rows.push(totalRow);

    return { columns: tableColumns, rows };
};
